/*
Tiny JSON fetch helper.

Why exist:
- Surfaces a useful error when the body isn't JSON (e.g. an HTML 404 page or
  a stale HTTP/SW cached entry from an earlier failing build). Browsers'
  default "JSON.parse: unexpected character at line 1 column 1" message is
  useless for diagnosis — this helper includes the URL and the first bytes
  of the actual body in the thrown error.
- Avoids the `cache: 'force-cache'` footgun. Caching is owned exclusively by
  the service worker (`/data/` is cache-first); the page-level fetch should
  not second-guess that, otherwise stale browser HTTP cache entries from
  earlier dev cycles can poison the response.
*/

/*
Fetch a URL and parse it as JSON. Throws a descriptive Error on HTTP
failure or non-JSON bodies; parsed value is typed as T (caller asserts).
*/
export async function fetchJson<T>(inUrl: string): Promise<T>
{
	const vResp = await fetch(inUrl)  // no cache option — let SW handle caching
	if (!vResp.ok)
	{
		throw new Error(`${inUrl}: HTTP ${vResp.status}`)
	}
	const vText = await vResp.text()  // grab as text first so we can show context on parse failure
	try
	{
		return JSON.parse(vText) as T
	}
	catch (vErr)
	{
		const vSnippet = vText.slice(0, 80).replace(/\s+/g, ' ').trim()  // shrink + sanitize for log
		throw new Error(`${inUrl}: invalid JSON (begins with: "${vSnippet}…")`)
	}
}
